import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  Avatar,
  Autocomplete,
  Stack,
} from '@mui/material';
import {
  Add,
  Search,
  Warehouse,
  SwapHoriz,
  Inventory,
  LocationOn,
  Phone,
  Person,
  ArrowForward,
  Download,
  Print,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { inventoryAPI, productsAPI, unitProductsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const WarehouseForm = ({ open, onClose, warehouse, onSaved }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    reset(warehouse || {});
  }, [warehouse, open, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await inventoryAPI.createWarehouse(data);
      toast.success('Warehouse created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save warehouse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{warehouse?.id ? 'Edit Warehouse' : 'Add Warehouse'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Warehouse Name *"
                {...register('name', { required: 'Name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" {...register('address')} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="City" {...register('city')} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Country" {...register('country')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone" {...register('phone')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const TransferDialog = ({ open, onClose, warehouses, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      product_id: '',
      from_warehouse_id: '',
      to_warehouse_id: '',
      quantity: 1,
      unit_ids: [],
      notes: '',
    },
  });

  const productId = watch('product_id');
  const fromWarehouseId = watch('from_warehouse_id');
  const selectedUnitIds = watch('unit_ids') || [];
  const quantityValue = watch('quantity');

  useEffect(() => {
    if (!open) return;
    productsAPI.getAll({ limit: 500 }).then((response) => {
      setProducts(response.data.data || []);
    }).catch(() => {});

    setAvailableUnits([]);
    reset({
      product_id: '',
      from_warehouse_id: '',
      to_warehouse_id: '',
      quantity: 1,
      unit_ids: [],
      notes: '',
    });
  }, [open, reset]);

  useEffect(() => {
    if (!open || !productId || !fromWarehouseId) {
      setAvailableUnits([]);
      setValue('unit_ids', []);
      return;
    }

    let active = true;
    setUnitsLoading(true);
    setValue('unit_ids', []);

    unitProductsAPI.getAll({
      page: 1,
      limit: 200,
      product_id: productId,
      warehouse_id: fromWarehouseId,
      status: 'in_stock',
    })
      .then((response) => {
        if (!active) return;
        setAvailableUnits(response.data.data || []);
      })
      .catch(() => {
        if (active) setAvailableUnits([]);
      })
      .finally(() => {
        if (active) setUnitsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, productId, fromWarehouseId, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const response = await inventoryAPI.transferStock({
        product_id: data.product_id,
        from_warehouse_id: data.from_warehouse_id,
        to_warehouse_id: data.to_warehouse_id,
        quantity: availableUnits.length > 0 ? selectedUnitIds.length : parseInt(data.quantity, 10),
        unit_ids: data.unit_ids || [],
        notes: data.notes,
      });
      const transferStatus = response?.data?.data?.status;
      if (transferStatus === 'pending_approval') {
        toast.info('Transfer request submitted for admin approval');
      } else {
        toast.success('Stock transferred successfully');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Transfer Stock Between Warehouses</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="product_id"
                control={control}
                rules={{ required: 'Product required' }}
                render={({ field }) => (
                  <Autocomplete
                    options={products}
                    getOptionLabel={(option) => `${option.brand_name || ''} ${option.name} (${option.sku})`}
                    value={products.find((product) => product.id === field.value) || null}
                    onChange={(_, value) => field.onChange(value ? value.id : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product / Vehicle *"
                        size="small"
                        error={!!errors.product_id}
                        helperText={errors.product_id?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>From Warehouse *</InputLabel>
                <Select {...register('from_warehouse_id', { required: true })} label="From Warehouse *" defaultValue="">
                  <MenuItem value="">- Select -</MenuItem>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForward color="primary" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>To Warehouse *</InputLabel>
                <Select {...register('to_warehouse_id', { required: true })} label="To Warehouse *" defaultValue="">
                  <MenuItem value="">- Select -</MenuItem>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="unit_ids"
                control={control}
                rules={availableUnits.length > 0 ? {
                  validate: (value) => (value?.length ? true : 'Select the units to transfer'),
                } : undefined}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableUnits}
                    loading={unitsLoading}
                    value={availableUnits.filter((unit) => (field.value || []).includes(unit.id))}
                    getOptionLabel={(unit) => [
                      unit.chassis_number,
                      unit.color,
                      unit.year,
                      unit.condition?.replace(/_/g, ' '),
                    ].filter(Boolean).join(' • ')}
                    onChange={(_, value) => {
                      const ids = value.map((unit) => unit.id);
                      field.onChange(ids);
                      setValue('quantity', ids.length || 1);
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tracked Units"
                        error={!!errors.unit_ids}
                        helperText={
                          errors.unit_ids?.message
                          || (availableUnits.length > 0
                            ? 'Move the exact units between warehouses'
                            : (productId && fromWarehouseId ? 'No tracked units found. Quantity transfer will be used.' : ''))
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Quantity *"
                type="number"
                size="small"
                {...register('quantity', { required: true, min: { value: 1, message: 'Min 1' }, valueAsNumber: true })}
                error={!!errors.quantity}
                helperText={errors.quantity?.message}
                inputProps={{ min: 1 }}
                disabled={availableUnits.length > 0}
                value={availableUnits.length > 0 ? selectedUnitIds.length : (quantityValue || '')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" size="small" {...register('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<SwapHoriz />} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Transfer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};


const WarehouseCard = ({ warehouse, onViewStock }) => {
  const navigate = useNavigate();
  return (
    <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <Warehouse />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={700}>{warehouse.name}</Typography>
            <Chip label={warehouse.is_active ? 'Active' : 'Inactive'} color={warehouse.is_active ? 'success' : 'default'} size="small" />
          </Box>
        </Box>

        {[
          { icon: <LocationOn fontSize="small" />, text: [warehouse.city, warehouse.country].filter(Boolean).join(', ') || '-' },
          { icon: <Phone fontSize="small" />, text: warehouse.phone || '-' },
          { icon: <Person fontSize="small" />, text: warehouse.manager_name || 'No manager assigned' },
        ].map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: 'text.secondary' }}>
            {item.icon}
            <Typography variant="body2">{item.text}</Typography>
          </Box>
        ))}

        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Products</Typography>
            <Typography variant="h6" fontWeight={700} color="primary">{warehouse.product_count || 0}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Total Units</Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">{warehouse.total_items || 0}</Typography>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
        <Button fullWidth variant="outlined" startIcon={<Inventory />} onClick={() => onViewStock(warehouse)}>
          View Stock
        </Button>
        <Button variant="contained" startIcon={<ArrowForward />} onClick={() => navigate(`/warehouses/${warehouse.id}`)}>
          Enter
        </Button>
      </CardActions>
    </Card>
  );
};

const WarehouseStockDialog = ({ open, onClose, warehouse }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !warehouse) return;
    setLoading(true);
    inventoryAPI.getAll({ warehouse_id: warehouse.id, limit: 200 })
      .then((response) => setInventory(response.data.data || []))
      .catch(() => toast.error('Failed to load stock'))
      .finally(() => setLoading(false));
  }, [open, warehouse]);

  const filtered = inventory.filter((item) =>
    !search
    || item.product_name?.toLowerCase().includes(search.toLowerCase())
    || item.sku?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontWeight={700}>{warehouse?.name} - Stock</Typography>
            <Typography variant="caption" color="text.secondary">{inventory.length} product lines</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search products..."
          sx={{ mb: 2 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.disabled' }} /> }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell align="right">In Stock</TableCell>
                <TableCell align="right">Reserved</TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell align="right">Min Qty</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No stock in this warehouse</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.product_name}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.sku}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={item.quantity}
                      color={item.quantity <= item.min_quantity ? 'error' : item.quantity <= item.min_quantity * 2 ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{item.reserved_quantity || 0}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {(item.quantity || 0) - (item.reserved_quantity || 0)}
                  </TableCell>
                  <TableCell align="right">{item.min_quantity || 0}</TableCell>
                  <TableCell>
                    {item.quantity <= item.min_quantity
                      ? <Chip label="Low Stock" color="error" size="small" />
                      : <Chip label="OK" color="success" size="small" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const WarehousesPage = () => {
  const { user } = useSelector((state) => state.auth);
  const canManage = user?.roles?.some((role) => ['admin', 'manager'].includes(role));
  const canApproveTransfers = user?.roles?.some((role) => ['admin'].includes(role));

  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState([]);
  const [movLoading, setMovLoading] = useState(false);
  const [transferRequests, setTransferRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [requestActionId, setRequestActionId] = useState(null);
  const [tab, setTab] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [stockWarehouse, setStockWarehouse] = useState(null);

  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await inventoryAPI.getWarehouses();
      setWarehouses(data.data || []);
    } catch {
      toast.error('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMovements = useCallback(async () => {
    try {
      setMovLoading(true);
      const { data } = await inventoryAPI.getMovements({ limit: 50 });
      setMovements(data.data || []);
    } catch {
      toast.error('Failed to load movements');
    } finally {
      setMovLoading(false);
    }
  }, []);

  const fetchTransferRequests = useCallback(async () => {
    try {
      setReqLoading(true);
      const { data } = await inventoryAPI.getTransferRequests({ limit: 100 });
      setTransferRequests(data.data || []);
    } catch {
      toast.error('Failed to load transfer requests');
    } finally {
      setReqLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    if (tab === 1) fetchMovements();
    if (tab === 2) fetchTransferRequests();
  }, [tab, fetchMovements, fetchTransferRequests]);

  const handleTransferSaved = useCallback(() => {
    fetchWarehouses();
    fetchMovements();
    fetchTransferRequests();
  }, [fetchWarehouses, fetchMovements, fetchTransferRequests]);

  const openWarehouseById = useCallback((warehouseId) => {
    const target = warehouses.find((warehouse) => warehouse.id === warehouseId);
    if (target) setStockWarehouse(target);
  }, [warehouses]);

  const loadAllMovements = useCallback(async () => {
    const { data } = await inventoryAPI.getMovements({ include_all: true, limit: 5000 });
    return data.data || [];
  }, []);

  const handleExportMovements = useCallback(async () => {
    try {
      const rows = await loadAllMovements();
      if (!rows.length) {
        toast.info('No movement data to export');
        return;
      }

      const columns = [
        ['Timestamp', (row) => new Date(row.created_at).toLocaleString()],
        ['Movement Type', (row) => row.movement_type || ''],
        ['Product', (row) => row.product_name || ''],
        ['SKU', (row) => row.sku || ''],
        ['Chassis', (row) => row.chassis_number || ''],
        ['Unit Type', (row) => row.unit_condition || ''],
        ['Quantity', (row) => row.quantity ?? ''],
        ['Source Warehouse', (row) => row.from_warehouse_name || row.warehouse_name || ''],
        ['Destination Warehouse', (row) => row.to_warehouse_name || ''],
        ['Initiator', (row) => row.performed_by_name || ''],
        ['Approver', (row) => row.approved_by_name || ''],
        ['Approved At', (row) => row.approved_at ? new Date(row.approved_at).toLocaleString() : ''],
        ['Reference', (row) => row.reference_type || ''],
        ['Notes', (row) => row.notes || ''],
      ];

      const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [
        columns.map(([label]) => escapeCsv(label)).join(','),
        ...rows.map((row) => columns.map(([, extractor]) => escapeCsv(extractor(row))).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-movements-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Stock movement history exported');
    } catch {
      toast.error('Failed to export stock movements');
    }
  }, [loadAllMovements]);

  const handlePrintMovements = useCallback(async () => {
    try {
      const rows = await loadAllMovements();
      if (!rows.length) {
        toast.info('No movement data to print');
        return;
      }

      const tableRows = rows.map((row) => `
        <tr>
          <td>${new Date(row.created_at).toLocaleString()}</td>
          <td>${(row.movement_type || '').toUpperCase()}</td>
          <td>${row.product_name || ''}</td>
          <td>${[row.sku, row.chassis_number, row.unit_condition].filter(Boolean).join(' • ')}</td>
          <td>${row.quantity || 0}</td>
          <td>${row.from_warehouse_name || row.warehouse_name || '-'}</td>
          <td>${row.to_warehouse_name || '-'}</td>
          <td>${row.performed_by_name || '-'}</td>
          <td>${row.approved_by_name || '-'}</td>
          <td>${row.approved_at ? new Date(row.approved_at).toLocaleString() : '-'}</td>
          <td>${row.notes || '-'}</td>
        </tr>
      `).join('');

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>Stock Movement History</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h2 { margin-bottom: 12px; }
              table { border-collapse: collapse; width: 100%; font-size: 12px; }
              th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
              th { background: #f3f4f6; }
            </style>
          </head>
          <body>
            <h2>Complete Stock Movement History</h2>
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Type</th>
                  <th>Product</th>
                  <th>Item Details / Unit Type</th>
                  <th>Qty</th>
                  <th>Source Warehouse</th>
                  <th>Destination Warehouse</th>
                  <th>Initiator</th>
                  <th>Approver</th>
                  <th>Approved At</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    } catch {
      toast.error('Failed to print stock movements');
    }
  }, [loadAllMovements]);

  const handleApproveRequest = useCallback(async (requestId) => {
    try {
      setRequestActionId(requestId);
      await inventoryAPI.approveTransferRequest(requestId);
      toast.success('Transfer request approved and executed');
      fetchMovements();
      fetchTransferRequests();
      fetchWarehouses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve request');
    } finally {
      setRequestActionId(null);
    }
  }, [fetchMovements, fetchTransferRequests, fetchWarehouses]);

  const handleRejectRequest = useCallback(async (requestId) => {
    const reason = window.prompt('Reason for rejection (optional):', '');
    if (reason === null) return;

    try {
      setRequestActionId(requestId);
      await inventoryAPI.rejectTransferRequest(requestId, { reason });
      toast.success('Transfer request rejected');
      fetchTransferRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject request');
    } finally {
      setRequestActionId(null);
    }
  }, [fetchTransferRequests]);

  const movementColor = {
    in: 'success',
    out: 'error',
    transfer: 'info',
    adjustment: 'warning',
    return: 'default',
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Warehouses</Typography>
          <Typography variant="body2" color="text.secondary">{warehouses.length} locations</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<SwapHoriz />} onClick={() => setTransferOpen(true)}>
            Transfer Stock
          </Button>
          {canManage && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
              Add Warehouse
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Warehouses" icon={<Warehouse />} iconPosition="start" />
        <Tab label="Stock Movements" icon={<SwapHoriz />} iconPosition="start" />
        <Tab label="Transfer Requests" icon={<SwapHoriz />} iconPosition="start" />
      </Tabs>

      {tab === 0 && (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={48} />
          </Box>
        ) : warehouses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Warehouse sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No warehouses yet</Typography>
            {canManage && (
              <Button variant="contained" sx={{ mt: 2 }} onClick={() => setCreateOpen(true)}>
                Add First Warehouse
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={3}>
            {warehouses.map((warehouse) => (
              <Grid item xs={12} sm={6} lg={4} key={warehouse.id}>
                <WarehouseCard warehouse={warehouse} onViewStock={setStockWarehouse} />
              </Grid>
            ))}
          </Grid>
        )
      )}

      {tab === 1 && (
        movLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'flex-end' }}>
              <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrintMovements}>
                Print History
              </Button>
              <Button size="small" variant="contained" startIcon={<Download />} onClick={handleExportMovements}>
                Export CSV
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Source Warehouse</TableCell>
                  <TableCell>Dest Warehouse</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Initiator</TableCell>
                  <TableCell>Approver</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(movement.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{movement.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[movement.sku, movement.chassis_number, movement.unit_condition].filter(Boolean).join(' \u2022 ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(movement.from_warehouse_name || movement.warehouse_name)
                        ? (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                            onClick={() => openWarehouseById(movement.from_warehouse_id || movement.warehouse_id)}
                          >
                            {movement.from_warehouse_name || movement.warehouse_name}
                          </Button>
                        )
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {movement.to_warehouse_name
                        ? (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                            onClick={() => openWarehouseById(movement.to_warehouse_id)}
                          >
                            {movement.to_warehouse_name}
                          </Button>
                        )
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={movement.movement_type.replace(/_/g, ' ').toUpperCase()}
                        color={movementColor[movement.movement_type] || 'default'}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        fontWeight={700}
                        color={movement.movement_type === 'out' ? 'error.main' : 'success.main'}
                      >
                        {movement.movement_type === 'out' ? '-' : '+'}{movement.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {movement.reference_type || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{movement.performed_by_name || '-'}</TableCell>
                    <TableCell>{movement.approved_by_name || '-'}</TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No movements recorded yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )
      )}

      {tab === 2 && (
        reqLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Requested At</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Requestor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Approver</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferRequests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(request.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{request.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{request.sku}</Typography>
                    </TableCell>
                    <TableCell>{request.from_warehouse_name || '-'}</TableCell>
                    <TableCell>{request.to_warehouse_name || '-'}</TableCell>
                    <TableCell align="right">{request.quantity}</TableCell>
                    <TableCell>{request.requested_by_name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={(request.status || '').replace(/_/g, ' ').toUpperCase()}
                        size="small"
                        color={
                          request.status === 'executed' ? 'success'
                            : request.status === 'pending_approval' ? 'warning'
                              : request.status === 'rejected' ? 'error'
                                : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{request.approved_by_name || '-'}</TableCell>
                    <TableCell>
                      {canApproveTransfers && request.status === 'pending_approval' ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            startIcon={<CheckCircle />}
                            disabled={requestActionId === request.id}
                            onClick={() => handleApproveRequest(request.id)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<Cancel />}
                            disabled={requestActionId === request.id}
                            onClick={() => handleRejectRequest(request.id)}
                          >
                            Reject
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {request.status === 'rejected' ? (request.rejection_reason || 'Rejected') : '-'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {transferRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No transfer requests found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )
      )}

      <WarehouseForm open={createOpen} onClose={() => setCreateOpen(false)} warehouse={null} onSaved={fetchWarehouses} />
      <TransferDialog open={transferOpen} onClose={() => setTransferOpen(false)} warehouses={warehouses} onSaved={handleTransferSaved} />
      <WarehouseStockDialog open={!!stockWarehouse} onClose={() => setStockWarehouse(null)} warehouse={stockWarehouse} />
    </Box>
  );
};

export default WarehousesPage;
